<template>
  <div class="pa-packages" :class="{ 'pa-packages--embedded': embedded }">
    <div v-if="!embedded" class="pa-packages__back-row">
      <router-link :to="backTo" class="pa-back">
        <i class="material-icons" aria-hidden="true">arrow_back</i>
        <span>Back to {{ backLabel }}</span>
      </router-link>
      <white-glove-admin-mode-toggle />
    </div>

    <!-- embedded in account settings (Change plan tab) the tab itself is the title -->
    <h1 v-if="!embedded" class="pa-h1">POP Authority Packages</h1>
    <p class="pa-sub">Month to month, cancel anytime. Everything we produce is yours to keep.</p>

    <!-- design PackageChooser `projects`: the project selector shows from a project and from the account -->
    <div v-if="projectOptions.length" class="pa-panel pa-packages__project">
      <i class="material-icons" aria-hidden="true">language</i>
      <div class="pa-packages__project-field">
        <label class="pa-packages__project-label" for="pa-packages-site">Subscribe for project</label>
        <select id="pa-packages-site" class="pa-sel" :value="String(siteId)" @change="switchSite($event.target.value)">
          <option v-for="p in projectOptions" :key="p.id" :value="String(p.id)" :disabled="!p.eligible">
            {{ p.name }}{{ p.note ? ` (${p.note})` : '' }}
          </option>
        </select>
      </div>
    </div>

    <!-- design: the guarantee strip above the cards -->
    <div class="pa-guarantee">
      <i class="material-icons" aria-hidden="true">trending_up</i>
      <b>Guaranteed growth in 90 days or we work free.</b>
      <a :href="guaranteeTermsUrl" target="_blank" rel="noopener noreferrer">(see terms)</a>
    </div>

    <div v-if="loading" class="pa-packages__loading">Loading…</div>
    <div v-else-if="loadError" class="pa-err">We could not load this project. Try again in a moment.</div>

    <template v-else>
      <!-- Closed project -->
      <div v-if="state.siteClosedAt && !liveSubscription" class="pa-note pa-note--gray">
        <i class="material-icons" aria-hidden="true">lock</i>
        <div>
          <b>This project is closed</b> ({{ closedReasonLabel }}).
          Your audit, content plans and Google folder stay available. Pick a package below to restart.
          <button v-if="isAdmin" type="button" class="pa-link" :disabled="busy" @click="reopen">Reopen project (admin)</button>
        </div>
      </div>

      <!-- Not eligible yet (mission control may assign once the review call is marked completed) -->
      <div v-else-if="!canSubscribe && !subscription && isAdmin && canAssign" class="pa-note">
        <i class="material-icons" aria-hidden="true">info</i>
        <div>
          <b>The review call is done, the audit is not delivered yet.</b>
          You can assign the package now: the first invoice is emailed right away, and the client sees it
          here and on the Overview once the audit is marked completed.
        </div>
      </div>
      <div v-else-if="!canSubscribe && !subscription" class="pa-note">
        <i class="material-icons" aria-hidden="true">info</i>
        <div>
          <b>Packages open once your audit is delivered.</b>
          {{ notEligibleCopy }}
        </div>
      </div>

      <!-- Invoice sent by the SEO Manager -->
      <div v-if="subscription && subscription.status === 'invoice_sent'" class="pa-invoice">
        <div class="pa-invoice__head">
          <i class="material-icons pa-invoice__icon" aria-hidden="true">receipt_long</i>
          <div class="pa-invoice__heading">
            <h3>{{ subscription.package.name }}: first monthly invoice</h3>
            <div class="pa-invoice__sub">
              Sent by your SEO Manager{{ subscription.invoiceSentAt ? ' on ' + formatDate(subscription.invoiceSentAt) : '' }}.
              Pay it within 7 days and month 1 starts right away.
            </div>
          </div>
          <div class="pa-invoice__state"><span class="pa-status pa-status--orange">Awaiting payment</span></div>
        </div>
        <div class="pa-invoice__body">
          <div class="pa-invrow"><span>{{ subscription.package.name }}, month 1</span><b>${{ money(subscription.package.price) }}</b></div>
          <div v-if="subscriptionCoupon" class="pa-invrow">
            <span>Coupon {{ subscriptionCoupon.code }} ({{ couponLabelOf(subscriptionCoupon) }})</span>
            <b class="pa-invrow__green">-${{ money(subscription.package.price - firstInvoiceAmount) }}</b>
          </div>
          <div class="pa-invrow"><span>Then every month until you cancel</span><b>{{ thenLineOf(subscription) }}</b></div>
          <div class="pa-invnote">
            The following months are charged automatically to the card you use for this invoice. You can change or
            cancel the package anytime from your project.
          </div>
          <div class="pa-invoice__actions">
            <a
              v-if="subscription.stripeHostedInvoiceUrl"
              class="pa-btn pa-btn--orange"
              :href="subscription.stripeHostedInvoiceUrl"
              target="_blank"
              rel="noopener noreferrer"
            >Pay invoice (${{ money(subscription.stripeHostedInvoiceAmount || subscription.package.price) }})</a>
            <button
              v-if="isAdmin"
              type="button"
              class="pa-btn pa-btn--out pa-btn--admin"
              :disabled="busy"
              @click="showMarkPaidConfirm = true"
            >
              <i class="material-icons" aria-hidden="true">task_alt</i>
              Mark paid (admin)
            </button>
          </div>
          <div class="pa-hint">
            Want a different package? Select it below and confirm: this invoice is replaced by one for the new package.
          </div>
        </div>
      </div>

      <!-- Live subscription summary -->
      <div v-if="liveSubscription" class="pa-current">
        <div class="pa-current__head">
          <div>
            <div class="pa-current__eyebrow">Current package</div>
            <h3 class="pa-current__name">{{ liveSubscription.package.name }}</h3>
          </div>
          <span class="pa-status pa-status--pill" :class="liveStatusClass">{{ liveStatusLabel }}</span>
        </div>
        <div class="pa-current__grid">
          <div>
            <span>Monthly price</span>
            <b>${{ money(liveMonthlyPrice) }}/mo</b>
            <small v-if="liveCouponHint" class="pa-current__coupon">{{ liveCouponHint }}</small>
          </div>
          <div><span>Content each month</span><b>{{ liveSubscription.package.silos_per_month }} Top Level + {{ liveSubscription.package.silos_per_month * liveSubscription.package.supporting_per_silo }} Supporting</b></div>
          <div><span>Campaign progress</span><b>{{ cycleLabel(liveSubscription) }}</b></div>
          <div><span>{{ liveSubscription.cancelAtPeriodEnd ? 'Ends on' : 'Next invoice' }}</span><b>{{ formatDate(liveSubscription.cancelAtPeriodEnd ? liveSubscription.currentPeriodEnd : liveSubscription.nextInvoiceAt) || '—' }}</b></div>
        </div>
        <div v-if="liveSubscription.pendingPackageKey && liveSubscription.pendingPackage" class="pa-hint">
          From next month: {{ liveSubscription.pendingPackage.name }} (${{ money(liveSubscription.pendingPackage.price) }}/mo).
        </div>
        <div class="pa-current__actions">
          <button
            v-if="liveSubscription.cancelAtPeriodEnd"
            type="button"
            class="pa-btn pa-btn--orange"
            :disabled="busy"
            @click="undoCancel"
          >Keep my subscription</button>
          <button
            v-else
            type="button"
            class="pa-btn pa-btn--out"
            :disabled="busy"
            @click="showCancelModal = true"
          >Cancel subscription</button>
          <template v-if="isAdmin">
            <button v-if="liveSubscription.status === 'paused'" type="button" class="pa-btn pa-btn--out pa-btn--admin" :disabled="busy" @click="resume">Resume (admin)</button>
            <button v-else type="button" class="pa-btn pa-btn--out pa-btn--admin" :disabled="busy" @click="pause">Pause (admin)</button>
          </template>
        </div>
        <div v-if="showChooser" class="pa-hint">Want a different mix? Select another package below and confirm the change.</div>
      </div>

      <!-- Chooser (design .pa-pkggrid) -->
      <package-chooser
        v-if="showChooser"
        v-model="selectedKey"
        :packages="catalogue.packages"
        :custom="catalogue.custom"
        :custom-tl="customTl"
        :custom-per="customPer"
        :current-key="currentSubscription ? currentSubscription.packageKey : ''"
        :disabled="busy"
        @update:custom-mix="onCustomMix"
      />

      <!-- design: "How billing works" on the left, "Your subscription" summary on the right -->
      <div v-if="showChooser" class="pa-packages__bottom">
        <div class="pa-packages__billing">
          <!-- Agreement: new subscriptions of we_publish sites that did not sign it in the setup wizard
               (signed there: the note under the subscribe button, design PackageChooser) -->
          <publishing-agreement
            v-if="state.agreementRequired && !currentSubscription"
            v-model="agreementAccepted"
            :site-name="site ? site.name : 'your site'"
            :site-url="site ? site.baseURL : ''"
            :disabled="busy"
          />
          <h3 class="pa-tlh">How billing works</h3>
          <p class="pa-packages__billing-text">
            Each month we bill your card and produce your next content cluster. Stop anytime.
          </p>
        </div>

        <div class="pa-summary">
          <h3 class="pa-summary__title">Your subscription</h3>
          <div class="pa-summary__line"><span>Package</span><b>{{ selectedPackage.name }}</b></div>
          <div v-if="blockedReason" class="pa-summary__blocked">
            Your custom mix matches <b>{{ blockedStandard.name }}</b>, so it is priced as that package.
            <button type="button" class="pa-summary__blocked-link" :disabled="busy" @click="selectedKey = blockedStandard.key">
              Select {{ shortName(blockedStandard) }}
            </button>
            to continue, or change the page mix.
          </div>
          <!-- the design's per-line price breakdown of a custom mix is left out on purpose (unit rates stay internal) -->
          <div class="pa-summary__line"><span>Monthly price</span><b>${{ money(selectedPackage.price) }}/mo</b></div>
          <div class="pa-summary__line"><span>Content each month</span><b>{{ selectedPackage.silos_per_month }} Top Level + {{ selectedPackage.silos_per_month * selectedPackage.supporting_per_silo }} Supporting pages</b></div>
          <div class="pa-summary__line"><span>Commitment</span><b>None, cancel anytime</b></div>
          <div class="pa-summary__line">
            <span>Setup fee &amp; audit</span>
            <b :class="setupFeeLine.paid ? 'pa-summary__paid' : 'pa-summary__due'">{{ setupFeeLine.label }}</b>
          </div>
          <!-- first invoice emailed, not paid: another package replaces the invoice -->
          <template v-if="unpaidSubscription">
            <div v-if="replacementCoupon" class="pa-summary__line">
              <span>
                Coupon {{ replacementCoupon.code }} ({{ couponLabelOf(replacementCoupon) }})
                <button v-if="appliedCoupon" type="button" class="pa-summary__blocked-link" :disabled="busy" @click="removeCoupon">Remove</button>
              </span>
              <b class="pa-summary__paid">-${{ money(replacementDiscount) }}</b>
            </div>
            <div class="pa-summary__line pa-summary__line--total">
              <span>{{ selectedIsCurrent && !appliedCoupon ? 'Your invoice (month 1)' : 'New invoice (month 1)' }}</span>
              <span>${{ money(replacementTotal) }}</span>
            </div>
            <p class="pa-summary__note">
              <template v-if="selectedIsCurrent && !appliedCoupon">
                This is the package on your unpaid invoice. Select another one to change it before you pay.
              </template>
              <template v-else>
                Your unpaid {{ unpaidSubscription.package.name }} invoice is canceled and a new one is emailed for this package.
              </template>
              {{ replacementThenNote }}
            </p>
            <template v-if="!appliedCoupon">
              <div v-if="!showCoupon" class="pa-summary__coupon-link">
                <button type="button" class="pa-link pa-summary__coupon-toggle" @click="showCoupon = true">Have a coupon?</button>
              </div>
              <div v-else class="pa-summary__coupon">
                <input
                  v-model="couponCode"
                  class="pa-in pa-summary__coupon-input"
                  placeholder="Coupon code"
                  :disabled="couponLoading"
                  @keydown.enter.prevent="applyCoupon"
                />
                <button type="button" class="pa-btn pa-btn--out" :disabled="!couponCode.trim() || couponLoading" @click="applyCoupon">Apply coupon</button>
                <span v-if="couponError" class="pa-err pa-err--inline">{{ couponError }}</span>
              </div>
            </template>
          </template>
          <template v-else-if="!liveSubscription">
            <div v-if="appliedCoupon" class="pa-summary__line">
              <span>
                Coupon {{ appliedCoupon.code }} ({{ couponLabel }})
                <button type="button" class="pa-summary__blocked-link" :disabled="busy" @click="removeCoupon">Remove</button>
              </span>
              <b class="pa-summary__paid">-${{ money(couponDiscount) }}</b>
            </div>
            <div class="pa-summary__line pa-summary__line--total"><span>Pay now (month 1)</span><span>${{ money(firstMonthTotal) }}</span></div>
            <p class="pa-summary__note">{{ thenNote }}</p>
            <!-- coupon box, as the setup fee step (design pa-setup.jsx InvoiceStep) -->
            <template v-if="canSubscribe && !appliedCoupon">
              <div v-if="!showCoupon" class="pa-summary__coupon-link">
                <button type="button" class="pa-link pa-summary__coupon-toggle" @click="showCoupon = true">Have a coupon?</button>
              </div>
              <div v-else class="pa-summary__coupon">
                <input
                  v-model="couponCode"
                  class="pa-in pa-summary__coupon-input"
                  placeholder="Coupon code"
                  :disabled="couponLoading"
                  @keydown.enter.prevent="applyCoupon"
                />
                <button type="button" class="pa-btn pa-btn--out" :disabled="!couponCode.trim() || couponLoading" @click="applyCoupon">Apply coupon</button>
                <span v-if="couponError" class="pa-err pa-err--inline">{{ couponError }}</span>
              </div>
            </template>
          </template>
          <template v-else>
            <div v-if="appliedCoupon" class="pa-summary__line">
              <span>
                Coupon {{ appliedCoupon.code }} ({{ couponLabel }})
                <button type="button" class="pa-summary__blocked-link" :disabled="busy" @click="removeCoupon">Remove</button>
              </span>
              <b class="pa-summary__paid">-${{ money(couponDiscount) }}/mo</b>
            </div>
            <div class="pa-summary__line pa-summary__line--total">
              <span>{{ changeIsUpgrade ? 'Applies now (prorated)' : 'Applies from next month' }}</span>
              <span>${{ money(selectedPackage.price) }}/mo</span>
            </div>
            <p class="pa-summary__note">
              {{ changeIsUpgrade
                ? 'Upgrades apply immediately: you are charged the prorated difference for the rest of this month and your quota rises today.'
                : 'Downgrades apply at your next invoice: this month keeps its current quota and price.' }}
              <template v-if="appliedCoupon">
                Your coupon starts with your next invoice: ${{ money(firstMonthTotal) }}/mo {{ couponDurationLabel }}.
              </template>
            </p>
            <!-- a coupon can come with a package change too; it replaces the current discount -->
            <template v-if="!selectedIsCurrent && !appliedCoupon">
              <div v-if="!showCoupon" class="pa-summary__coupon-link">
                <button type="button" class="pa-link pa-summary__coupon-toggle" @click="showCoupon = true">Have a coupon?</button>
              </div>
              <div v-else class="pa-summary__coupon">
                <input
                  v-model="couponCode"
                  class="pa-in pa-summary__coupon-input"
                  placeholder="Coupon code"
                  :disabled="couponLoading"
                  @keydown.enter.prevent="applyCoupon"
                />
                <button type="button" class="pa-btn pa-btn--out" :disabled="!couponCode.trim() || couponLoading" @click="applyCoupon">Apply coupon</button>
                <span v-if="couponError" class="pa-err pa-err--inline">{{ couponError }}</span>
              </div>
            </template>
          </template>

          <div class="pa-summary__actions">
            <template v-if="unpaidSubscription">
              <button
                type="button"
                class="pa-btn pa-btn--orange"
                :disabled="busy || Boolean(blockedReason) || !replacementChanges"
                @click="showReplaceConfirm = true"
              >{{ replacementButtonLabel }}</button>
            </template>
            <template v-else-if="liveSubscription">
              <button
                type="button"
                class="pa-btn pa-btn--orange"
                :disabled="busy || Boolean(blockedReason) || selectedIsCurrent"
                @click="showChangeConfirm = true"
              >{{ selectedIsCurrent ? 'Current package' : 'Change package' }}</button>
            </template>
            <template v-else>
              <button
                v-if="!isAdmin || !canAssign"
                type="button"
                class="pa-btn pa-btn--orange"
                :disabled="busy || !canSubscribe || Boolean(blockedReason) || (state.agreementRequired && !agreementAccepted)"
                @click="subscribe"
              >{{ state.agreementRequired ? 'Sign & subscribe' : 'Subscribe' }}: ${{ money(selectedPackage.price) }}/mo</button>
              <template v-else>
                <button
                  v-if="canSubscribe"
                  type="button"
                  class="pa-btn pa-btn--orange"
                  :disabled="busy || Boolean(blockedReason) || (state.agreementRequired && !agreementAccepted)"
                  @click="subscribe"
                >Subscribe as the client: ${{ money(selectedPackage.price) }}/mo</button>
                <button
                  type="button"
                  class="pa-btn pa-btn--out pa-btn--admin"
                  :disabled="busy || Boolean(blockedReason)"
                  @click="showAssignConfirm = true"
                >
                  <i class="material-icons" aria-hidden="true">send</i>
                  Assign &amp; send invoice (admin)
                </button>
              </template>
            </template>
          </div>
          <p v-if="!liveSubscription && state.agreementSignedAt && !state.agreementRequired" class="pa-summary__foot">
            You signed the Website Publishing Agreement during setup. Next: your first invoice.
          </p>
        </div>
      </div>
    </template>

    <!-- Change package confirm (design pa-app.jsx Modal: icon, muted body, Cancel + orange confirm) -->
    <div v-if="showChangeConfirm" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showChangeConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">swap_horiz</i></div>
        <h3 class="pa-cmodal__title">Change to {{ selectedPackage.name }}?</h3>
        <p v-if="changeIsUpgrade" class="pa-cmodal__body">
          Your package changes now. Stripe charges the prorated difference for the rest of this month
          and your monthly price becomes ${{ money(selectedPackage.price) }} from the next invoice.
          The content plan quota rises immediately.
        </p>
        <p v-else class="pa-cmodal__body">
          Your current month keeps its quota and price. From the next invoice you pay
          ${{ money(selectedPackage.price) }}/mo and the {{ selectedPackage.name }} quota applies.
        </p>
        <div v-if="appliedCoupon" class="pa-cmodal__rows">
          <div class="pa-cmodal__row"><span>{{ selectedPackage.name }}</span><b>${{ money(selectedPackage.price) }}/mo</b></div>
          <div class="pa-cmodal__row"><span>Coupon {{ appliedCoupon.code }} ({{ couponLabel }})</span><b>-${{ money(couponDiscount) }}/mo</b></div>
          <div class="pa-cmodal__row"><span><b>From your next invoice</b></span><b>${{ money(firstMonthTotal) }}/mo</b></div>
        </div>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showChangeConfirm = false">Keep current</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="changePackage">Yes, change package</button>
        </div>
      </div>
    </div>

    <!-- Unpaid first invoice: replace it (same design modal) -->
    <div v-if="showReplaceConfirm && unpaidSubscription" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showReplaceConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">swap_horiz</i></div>
        <h3 class="pa-cmodal__title">{{ selectedIsCurrent ? 'Update your invoice?' : `Change to ${selectedPackage.name}?` }}</h3>
        <p class="pa-cmodal__body">
          Your unpaid {{ unpaidSubscription.package.name }} invoice is canceled and its payment link stops working.
          A new first invoice is emailed right away, with 7 days to pay. Month 1 starts as soon as it is paid.
        </p>
        <div class="pa-cmodal__rows">
          <div class="pa-cmodal__row">
            <span>{{ selectedPackage.name }} (month 1, billed monthly)</span><b>${{ money(selectedPackage.price) }}</b>
          </div>
          <template v-if="replacementCoupon">
            <div class="pa-cmodal__row"><span>Coupon {{ replacementCoupon.code }} ({{ couponLabelOf(replacementCoupon) }})</span><b>-${{ money(replacementDiscount) }}</b></div>
            <div class="pa-cmodal__row"><span><b>New invoice</b></span><b>${{ money(replacementTotal) }}</b></div>
          </template>
        </div>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showReplaceConfirm = false">Keep current</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="changePackage">{{ selectedIsCurrent ? 'Yes, update invoice' : 'Yes, change package' }}</button>
        </div>
      </div>
    </div>

    <!-- Cancel survey -->
    <cancel-subscription-modal
      v-if="showCancelModal && liveSubscription"
      :subscription="liveSubscription"
      :busy="busy"
      @close="showCancelModal = false"
      @confirm="cancelSubscription"
    />

    <!-- Admin: assign confirm (design pa-app.jsx sendInvoice("package") / viewPkgInvoice: send icon, price row) -->
    <div v-if="showAssignConfirm && isAdmin" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showAssignConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">send</i></div>
        <h3 class="pa-cmodal__title">Assign {{ selectedPackage.name }} and send the first invoice?</h3>
        <p class="pa-cmodal__body">
          A Stripe subscription is created for {{ ownerEmail }} and the first monthly invoice is emailed with
          7 days to pay. Renewals are charged automatically to the card used for that payment.
        </p>
        <!-- no per-line breakdown of a custom mix: unit rates stay internal, like in the summary -->
        <div class="pa-cmodal__rows">
          <div class="pa-cmodal__row">
            <span>{{ selectedPackage.name }} (month 1, billed monthly)</span><b>${{ money(selectedPackage.price) }}</b>
          </div>
          <template v-if="appliedCoupon">
            <div class="pa-cmodal__row"><span>Coupon {{ appliedCoupon.code }} ({{ couponLabel }})</span><b>-${{ money(couponDiscount) }}</b></div>
            <div class="pa-cmodal__row"><span><b>First invoice</b></span><b>${{ money(firstMonthTotal) }}</b></div>
          </template>
        </div>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showAssignConfirm = false">Cancel</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="assign">Send invoice</button>
        </div>
      </div>
    </div>

    <!-- Admin: mark paid confirm (design DemoPanel "Mark invoice paid (admin)": price_check) -->
    <div v-if="showMarkPaidConfirm && isAdmin" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showMarkPaidConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">price_check</i></div>
        <h3 class="pa-cmodal__title">Mark the first invoice as paid?</h3>
        <p class="pa-cmodal__body">
          Use this when the client paid outside Stripe. The invoice is marked paid out of band, month 1 starts
          and the payment confirmation email goes out.
        </p>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showMarkPaidConfirm = false">Cancel</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="markPaid">Mark paid</button>
        </div>
      </div>
    </div>

    <async-tasks-popup v-if="siteId && !embedded" :site-id="siteId" :campaign-id="0" />
    <!-- this page lives outside the POP Authority layout (own route, account settings): its own toast host -->
    <pa-toast />
  </div>
</template>

<script>
import AsyncTasksPopup from '@/components/AsyncTasksPopup.vue'
import WhiteGloveAdminModeToggle from '@/components/SitesSingleView/WhiteGlove/WhiteGloveAdminModeToggle.vue'
import PackageChooser from '@/components/SitesSingleView/PopAuthority/PackageChooser.vue'
import PublishingAgreement from '@/components/SitesSingleView/PopAuthority/PublishingAgreement.vue'
import CancelSubscriptionModal from '@/components/SitesSingleView/PopAuthority/CancelSubscriptionModal.vue'
import PaToast from '@/components/SitesSingleView/PopAuthority/PaToast.vue'
import {
  POP_AUTHORITY_CUSTOM_DEFAULTS,
  POP_AUTHORITY_GUARANTEE_TERMS_URL,
  POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES,
  POP_AUTHORITY_PACKAGES,
  POP_AUTHORITY_PACKAGES_COUPON_PRODUCT,
  popAuthorityCampaignProgressLabel,
  popAuthorityCouponCoversCycle,
  popAuthorityCouponDurationLabel,
  popAuthorityCouponFromSubscription,
  popAuthorityCouponFromValidated,
  popAuthorityCouponLabel,
  popAuthorityCouponThenNote,
  popAuthorityCustomQuote,
  popAuthorityCouponExceedsPrice,
  popAuthorityCouponPrice,
  popAuthorityMoney,
  popAuthorityPackageShortName,
  popAuthoritySubscriptionStatusLabel,
  showPopAuthorityToast,
} from '@/utils/popAuthority'

const COUPON_OVER_PRICE_ERROR = 'This coupon is worth more than this package costs per month.'

const NOT_ELIGIBLE_COPY = {
  audit_not_delivered: 'Your SEO Manager is still working on your audit and content plans. You pick a package on your review call.',
  not_pop_authority: 'This account is not a POP Authority account.',
  site_deleted: 'This project was deleted.',
  subscription_live: 'This project already has an active package.',
  invoice_sent: 'Your first package invoice is waiting for payment.',
}

const CLOSED_REASON_LABELS = {
  user_declined: 'closed by your SEO Manager',
  no_package_60d: 'no package started within 60 days of the audit',
  canceled: 'subscription ended',
}

function parseApiDate (value) {
  if (!value) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/.exec(String(value))
  if (!m) return null
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)))
}

/**
 * POP Authority packages page (design pa-tabs.jsx PackageChooser + Overview package widget):
 * subscribe (Stripe Checkout), pay the admin-sent first invoice, change or cancel the live
 * package, admin assign / mark paid / pause / resume / reopen.
 */
export default {
  name: 'PopAuthorityPackages',
  components: {
    AsyncTasksPopup, CancelSubscriptionModal, PackageChooser, PaToast, PublishingAgreement, WhiteGloveAdminModeToggle,
  },
  props: {
    /**
     * Account settings > Billing and payments > Plan: same chooser without the page chrome; the
     * project comes from the selector (local state) instead of the route.
     */
    embedded: { type: Boolean, default: false },
    /** Embedded: the project to open on (Overview "Change package"); falls back to the first eligible one. */
    initialSiteId: { type: Number, default: null },
  },
  data () {
    return {
      // embedded mode: the project picked in "Subscribe for project"
      embeddedSiteId: null,
      loading: true,
      loadError: false,
      busy: false,
      catalogue: { packages: POP_AUTHORITY_PACKAGES.map((p) => ({ ...p })), custom: POP_AUTHORITY_CUSTOM_DEFAULTS },
      state: {},
      eligibleSites: [],
      accountSubscriptions: [],
      guaranteeTermsUrl: POP_AUTHORITY_GUARANTEE_TERMS_URL,
      selectedKey: 'growth',
      customTl: 3,
      customPer: 3,
      agreementAccepted: false,
      showChangeConfirm: false,
      showReplaceConfirm: false,
      showCancelModal: false,
      showAssignConfirm: false,
      showMarkPaidConfirm: false,
      // percent coupon on the package (POP Authority package coupons)
      showCoupon: false,
      couponCode: '',
      couponLoading: false,
      couponError: '',
      appliedCoupon: null,
    }
  },
  computed: {
    siteId () {
      if (this.embedded) return this.embeddedSiteId
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    site () {
      return this.siteId ? (this.$store.getters.siteById(this.siteId) || null) : null
    },
    /** Opened outside a project (header button, account tab): "Back to My projects" / account settings. */
    fromAccount () {
      return this.embedded || ['account', 'account-settings'].includes(this.$route.query.from)
    },
    backTo () {
      if (this.$route.query.from === 'account-settings' && this.$store.state.user) {
        return `/useraccount/${this.$store.state.user.id}?mainTab=billing-and-payments&subTab=pop-authority-packages`
      }
      if (this.fromAccount) return '/sites'
      return { name: 'OverviewWhiteGlove', params: { siteId: String(this.$route.params.siteId) } }
    },
    backLabel () {
      if (this.$route.query.from === 'account-settings') return 'account settings'
      if (this.fromAccount) return 'My projects'
      return (this.site && this.site.name) ? this.site.name : 'your project'
    },
    /**
     * "Subscribe for project" options (design PackageChooser `projects`): eligible projects, projects
     * already on a package (disabled, "current: ...") and the open project when it is neither.
     */
    projectOptions () {
      const options = []
      const seen = new Set()
      this.eligibleSites.forEach((s) => {
        seen.add(String(s.id))
        options.push({ id: s.id, name: s.name, eligible: true, note: '' })
      })
      this.accountSubscriptions.forEach((sub) => {
        const id = String(sub.siteId)
        if (seen.has(id)) return
        seen.add(id)
        const live = POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES.includes(sub.status) || sub.status === 'invoice_sent'
        options.push({
          id: sub.siteId,
          name: sub.siteName || `Project #${sub.siteId}`,
          eligible: false,
          note: live && sub.package ? `current: ${sub.package.name}` : 'audit not delivered yet',
        })
      })
      if (this.siteId && !seen.has(String(this.siteId))) {
        options.push({
          id: this.siteId,
          name: (this.site && this.site.name) || `Project #${this.siteId}`,
          eligible: this.canSubscribe,
          note: this.canSubscribe ? '' : 'audit not delivered yet',
        })
      }
      return options.sort((a, b) => String(a.name).localeCompare(String(b.name)))
    },
    /**
     * "Setup fee & audit" summary line: the design assumes the fee is paid (packages open after
     * the audit), the page can be opened earlier, so the line follows the real order state.
     */
    setupFeeLine () {
      const status = this.state.setupFeeStatus
      const amount = this.state.setupFeeAmount
      const money = amount != null ? ` ($${this.money(amount)})` : ''
      // design: "Already paid", no amount; an open invoice keeps its amount (what is still due)
      if (status === 'paid') return { paid: true, label: 'Already paid' }
      if (status === 'invoice_sent') return { paid: false, label: `Invoice sent, not paid yet${money}` }
      return { paid: false, label: 'Not paid yet' }
    },
    /** The standard package a custom mix duplicates (design: the summary asks to select it). */
    blockedStandard () {
      const key = this.customQuote.matchesStandardKey
      return (key && this.catalogue.packages.find((p) => p.key === key)) || { key: 'growth', name: 'that package' }
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isAdmin () {
      return Boolean(this.state.isAdmin) && this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    /** The billing email the invoice goes to: the owner of the account being worked on (mission control acts through a relation), preferring its "Send invoices to" address. */
    ownerEmail () {
      const r = this.$store.state.selectedSubAccountRelation
      const user = this.$store.state.user
      return (r && (r.agencyInvoicingEmail || r.agencyUserEmail)) || (user && (user.invoicingEmail || user.email)) || 'the site owner'
    },
    subscription () {
      return this.state.subscription || null
    },
    liveSubscription () {
      const s = this.subscription
      return s && POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES.includes(s.status) ? s : null
    },
    /** First invoice emailed and not paid yet: the package can still be replaced. */
    unpaidSubscription () {
      const s = this.subscription
      return s && s.status === 'invoice_sent' && s.package ? s : null
    },
    /** The subscription whose package the chooser marks as current (live, or invoiced and unpaid). */
    currentSubscription () {
      return this.liveSubscription || this.unpaidSubscription
    },
    /** Unpaid invoice: the coupon of the new invoice (typed here, else the one already on the invoice, kept). */
    replacementCoupon () {
      return this.appliedCouponInfo || (this.unpaidSubscription ? this.subscriptionCoupon : null)
    },
    replacementTotal () {
      const price = Number(this.selectedPackage.price) || 0
      // the invoiced package with its own coupon: the amount Stripe put on the invoice
      if (this.selectedIsCurrent && !this.appliedCoupon) return this.firstInvoiceAmount
      return this.replacementCoupon ? popAuthorityCouponPrice(price, this.replacementCoupon) : price
    },
    replacementDiscount () {
      return Math.max(0, (Number(this.selectedPackage.price) || 0) - this.replacementTotal)
    },
    replacementThenNote () {
      return popAuthorityCouponThenNote(this.selectedPackage.price, this.replacementCoupon)
    },
    /** Something to replace: another package, or the same one with another coupon. */
    replacementChanges () {
      if (!this.selectedIsCurrent) return true
      if (!this.appliedCouponInfo) return false
      const current = this.subscriptionCoupon
      return !current || String(current.code).toLowerCase() !== String(this.appliedCouponInfo.code).toLowerCase()
    },
    replacementButtonLabel () {
      if (!this.selectedIsCurrent) return 'Change package'
      return this.replacementChanges ? 'Update invoice with coupon' : 'Package on your invoice'
    },
    /** The coupon applied on this page (validated payload normalised: code, percentOff, duration...). */
    appliedCouponInfo () {
      return this.appliedCoupon ? popAuthorityCouponFromValidated(this.appliedCoupon.code, this.appliedCoupon.coupon) : null
    },
    couponLabel () {
      return popAuthorityCouponLabel(this.appliedCouponInfo)
    },
    couponDurationLabel () {
      return popAuthorityCouponDurationLabel(this.appliedCouponInfo).replace('on your first invoice', 'on that invoice')
    },
    firstMonthTotal () {
      const price = Number(this.selectedPackage.price) || 0
      return this.appliedCouponInfo ? popAuthorityCouponPrice(price, this.appliedCouponInfo) : price
    },
    couponDiscount () {
      return Math.max(0, (Number(this.selectedPackage.price) || 0) - this.firstMonthTotal)
    },
    thenNote () {
      return popAuthorityCouponThenNote(this.selectedPackage.price, this.appliedCouponInfo)
    },
    /** The coupon stamped on the site's subscription (unpaid invoice card, live summary). */
    subscriptionCoupon () {
      return popAuthorityCouponFromSubscription(this.subscription)
    },
    firstInvoiceAmount () {
      const s = this.subscription || {}
      if (s.stripeHostedInvoiceAmount !== null && s.stripeHostedInvoiceAmount !== undefined) {
        return Number(s.stripeHostedInvoiceAmount) || 0
      }
      const price = Number(s.package && s.package.price) || 0
      return this.subscriptionCoupon ? popAuthorityCouponPrice(price, this.subscriptionCoupon) : price
    },
    liveMonthlyPrice () {
      const s = this.liveSubscription
      if (!s) return 0
      const price = Number(s.package.price) || 0
      const coupon = this.subscriptionCoupon
      return coupon && popAuthorityCouponCoversCycle(coupon, s.currentCycle)
        ? popAuthorityCouponPrice(price, coupon)
        : price
    },
    liveCouponHint () {
      const s = this.liveSubscription
      const coupon = this.subscriptionCoupon
      if (!s || !coupon) return ''
      const cycle = Number(s.currentCycle) || 1
      if (popAuthorityCouponCoversCycle(coupon, cycle)) {
        const progress = coupon.duration === 'repeating'
          ? ` (month ${cycle - coupon.startCycle + 1} of ${coupon.durationInMonths})`
          : ''
        return `Coupon ${coupon.code}: ${popAuthorityCouponLabel(coupon)}${progress}`
      }
      // applied with a package change: the discount starts with the next invoice
      if (coupon.startCycle === cycle + 1) {
        return `Coupon ${coupon.code}: ${popAuthorityCouponLabel(coupon)}, from your next invoice`
      }
      return ''
    },
    canSubscribe () {
      return Boolean(this.state.canSubscribe)
    },
    /** Mission control may assign a package: as canSubscribe, or the review call is done while the audit is still open. */
    canAssign () {
      return Boolean(this.state.canAssign)
    },
    notEligibleCopy () {
      return NOT_ELIGIBLE_COPY[this.state.canSubscribeReason] || 'Come back once your review call is done.'
    },
    closedReasonLabel () {
      return CLOSED_REASON_LABELS[this.state.siteClosedReason] || 'closed'
    },
    showChooser () {
      // account settings without a project to pick yet: the cards stay visible, the actions are disabled
      if (this.embedded && !this.siteId) return true
      if (this.liveSubscription) return this.liveSubscription.status !== 'paused'
      // first invoice unpaid: another package can still be picked, it replaces the invoice
      if (this.unpaidSubscription) return true
      return this.canSubscribe || (this.isAdmin && this.canAssign)
    },
    customQuote () {
      return popAuthorityCustomQuote(this.customTl, this.customPer, this.catalogue.custom)
    },
    selectedPackage () {
      if (this.selectedKey === 'custom') {
        const q = this.customQuote
        // design PackageChooser `pkg = isStd || custom`: a mix that equals a standard package is sold
        // as that package (the server resolves it the same way), so the summary shows its name and
        // price like the card does, not the sum of the unit rates
        const twin = q.matchesStandardKey && this.catalogue.packages.find((p) => p.key === q.matchesStandardKey)
        if (twin) {
          return { key: 'custom', name: twin.name, price: twin.price, silos_per_month: q.tl, supporting_per_silo: q.per }
        }
        return { key: 'custom', name: 'Authority Custom', price: q.price, silos_per_month: q.tl, supporting_per_silo: q.per }
      }
      return this.catalogue.packages.find((p) => p.key === this.selectedKey) || this.catalogue.packages[0] || {}
    },
    selectedIsCurrent () {
      const live = this.currentSubscription
      if (!live || !live.package) return false
      if (live.packageKey !== this.selectedKey) return false
      if (this.selectedKey !== 'custom') return true
      return Number(live.package.tl) === this.customTl && Number(live.package.per) === this.customPer
    },
    changeIsUpgrade () {
      const live = this.liveSubscription
      if (!live || !live.package) return false
      return Number(this.selectedPackage.price) > Number(live.package.price)
    },
    /** Design: a custom mix that duplicates a standard package blocks the submit until the standard one is picked. */
    blockedReason () {
      if (this.selectedKey === 'custom' && this.customQuote.matchesStandardKey) {
        const std = this.catalogue.packages.find((p) => p.key === this.customQuote.matchesStandardKey)
        return `This mix is our ${std ? std.name : 'standard'} package. Select it to continue.`
      }
      return ''
    },
    liveStatusLabel () {
      return popAuthoritySubscriptionStatusLabel(this.liveSubscription)
    },
    liveStatusClass () {
      const s = this.liveSubscription
      if (!s) return ''
      if (s.status === 'active' && !s.cancelAtPeriodEnd) return 'pa-status--green'
      return 'pa-status--orange'
    },
  },
  watch: {
    siteId () {
      this.removeCoupon()
      this.load()
    },
    /** Another package picked after an amount coupon was applied: the coupon must still fit its price. */
    'selectedPackage.price' (price) {
      if (!popAuthorityCouponExceedsPrice(price, this.appliedCouponInfo)) return
      this.removeCoupon()
      this.showCoupon = true
      this.couponError = COUPON_OVER_PRICE_ERROR
    },
    '$store.getters.isPopAuthorityUser': {
      // Loads once the account type is known; White Glove sessions never use this page
      handler (v, old) {
        if (v === false && this.$store.state.user && this.$store.state.user.id) this.redirectToOverview()
        if (v && !old) this.load()
      },
      immediate: true,
    },
  },
  methods: {
    // package prices can carry cents (Authority Custom mixes land on .50)
    money: popAuthorityMoney,
    formatDate (value) {
      const d = parseApiDate(value)
      return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
    },
    cycleLabel (sub) {
      return popAuthorityCampaignProgressLabel(sub.currentCycle, sub.cycleAnchorAt)
    },
    shortName: popAuthorityPackageShortName,
    redirectToOverview () {
      if (this.embedded) return // the account page only renders the chooser for POP Authority accounts
      const siteId = this.$route.params.siteId
      if (siteId == null || siteId === '') {
        this.$router.replace('/sites')
        return
      }
      this.$router.replace({ name: 'OverviewWhiteGlove', params: { siteId: String(siteId) } })
    },
    switchSite (id) {
      if (this.embedded) {
        const n = parseInt(id, 10)
        if (Number.isFinite(n) && n !== this.embeddedSiteId) this.embeddedSiteId = n // the siteId watcher reloads
        return
      }
      if (!id || String(id) === String(this.$route.params.siteId)) return
      this.$router.replace({ name: 'PopAuthorityPackages', params: { siteId: String(id) }, query: this.$route.query })
    },
    onCustomMix ({ tl, per }) {
      this.customTl = tl
      this.customPer = per
    },
    ensureSiteLoaded () {
      // embedded: project names come from the subscriptions list, the store's sites are left alone
      if (this.embedded || !this.siteId || this.site) return Promise.resolve()
      return this.$store.dispatch('loadSite', this.siteId).then((response) => {
        if (response.data && response.data.status === 'SUCCESS' && response.data.site) {
          this.$store.commit('setSite', response.data.site)
        }
      }).catch(() => {})
    },
    /**
     * Embedded, no project picked yet: read the account's projects and open the first one that can
     * take a package (else the first with a subscription). Without any, only the catalogue loads.
     */
    loadEmbeddedProject () {
      this.loading = true
      this.loadError = false
      return Promise.all([
        this.$store.dispatch('getPopAuthoritySubscriptions').catch(() => null),
        this.$store.dispatch('getPopAuthorityPackages').catch(() => null),
      ]).then(([listRes, catalogueRes]) => {
        const list = listRes && listRes.data
        if (list && list.status === 'SUCCESS') {
          this.eligibleSites = list.eligibleSites || []
          this.accountSubscriptions = list.subscriptions || []
        }
        const known = new Set([
          ...this.eligibleSites.map((s) => String(s.id)),
          ...this.accountSubscriptions.map((sub) => String(sub.siteId)),
        ])
        const first = this.eligibleSites[0] || null
        const firstSub = this.accountSubscriptions[0] || null
        const wanted = this.initialSiteId && known.has(String(this.initialSiteId)) ? this.initialSiteId : null
        const id = parseInt(wanted || (first ? first.id : (firstSub && firstSub.siteId)), 10)
        if (Number.isFinite(id)) {
          this.embeddedSiteId = id // the siteId watcher runs the normal load
          return
        }
        const catalogue = catalogueRes && catalogueRes.data
        if (catalogue && catalogue.status === 'SUCCESS') {
          this.catalogue = {
            packages: catalogue.packages || this.catalogue.packages,
            custom: { ...POP_AUTHORITY_CUSTOM_DEFAULTS, ...(catalogue.custom || {}) },
          }
        }
        this.loading = false
      })
    },
    load () {
      if (!this.$store.getters.isPopAuthorityUser) {
        this.loading = false
        return Promise.resolve()
      }
      if (this.embedded && !this.siteId) return this.loadEmbeddedProject()
      if (!this.siteId) {
        this.loading = false
        return Promise.resolve()
      }
      this.loading = true
      this.loadError = false
      const tasks = [
        this.$store.dispatch('getPopAuthorityPackages'),
        this.$store.dispatch('getPopAuthoritySiteSubscription', { siteId: this.siteId }),
        this.ensureSiteLoaded(),
        // projects of the "Subscribe for project" selector
        this.$store.dispatch('getPopAuthoritySubscriptions').catch(() => null),
      ]
      return Promise.all(tasks)
        .then(([catalogueRes, stateRes, _site, listRes]) => {
          const catalogue = catalogueRes && catalogueRes.data
          if (catalogue && catalogue.status === 'SUCCESS') {
            this.catalogue = {
              packages: catalogue.packages || this.catalogue.packages,
              custom: { ...POP_AUTHORITY_CUSTOM_DEFAULTS, ...(catalogue.custom || {}) },
            }
            const defaults = this.catalogue.custom.defaults || POP_AUTHORITY_CUSTOM_DEFAULTS.defaults
            if (!this.subscription) {
              this.customTl = defaults.tl
              this.customPer = defaults.per
            }
          }
          const state = stateRes && stateRes.data
          if (!state || state.status !== 'SUCCESS') {
            this.loadError = true
            return
          }
          this.state = state
          this.applySelectionFromState()
          const list = listRes && listRes.data
          if (list && list.status === 'SUCCESS') {
            this.eligibleSites = list.eligibleSites || []
            this.accountSubscriptions = list.subscriptions || []
          }
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    applySelectionFromState () {
      const live = this.currentSubscription
      if (live && live.package) {
        this.selectedKey = live.packageKey || 'growth'
        if (live.packageKey === 'custom') {
          this.customTl = Number(live.package.tl) || this.customTl
          this.customPer = Number(live.package.per) || this.customPer
        }
        return
      }
      const key = this.$route.query.package
      if (key && (key === 'custom' || this.catalogue.packages.some((p) => p.key === key))) this.selectedKey = key
    },
    selectionBody () {
      const body = { packageKey: this.selectedKey }
      if (this.selectedKey === 'custom') body.custom = { tl: this.customTl, per: this.customPer }
      return body
    },
    run (action, payload, successText) {
      if (this.busy) return Promise.resolve(null)
      this.busy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch(action, { siteId: this.siteId, ...payload })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            // design: confirmations use the teal toast; errors stay red notifications
            if (successText) showPopAuthorityToast(this, successText)
            return data
          }
          this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Something went wrong' })
          return null
        })
        .catch((error) => {
          const msg = error?.response?.data?.msg || 'Something went wrong'
          this.$notify({ group: 'info', type: 'error', text: msg })
          return null
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.busy = false
        })
    },
    applyState (data) {
      if (data && data.subscription !== undefined) {
        this.state = data
        this.applySelectionFromState()
      } else {
        this.load()
      }
    },
    couponLabelOf (coupon) {
      return popAuthorityCouponLabel(coupon)
    },
    /** The unpaid-invoice card's "Then every month" value from the subscription's coupon. */
    thenLineOf (subscription) {
      const price = Number(subscription.package.price) || 0
      const coupon = popAuthorityCouponFromSubscription(subscription)
      const full = `$${this.money(price)}/mo`
      if (!coupon || !popAuthorityCouponCoversCycle(coupon, 2)) return full
      const reduced = `$${this.money(popAuthorityCouponPrice(price, coupon))}/mo`
      if (coupon.duration === 'repeating') {
        const more = (Number(coupon.durationInMonths) || 0) - 1
        return `${reduced} for ${more === 1 ? '1 more month' : `${more} more months`}, then ${full}`
      }
      return reduced
    },
    /** Same check as the setup fee step: one Percent Off coupon on the packages product; the server re-validates. */
    applyCoupon () {
      const code = this.couponCode.trim()
      if (!code || this.couponLoading) return Promise.resolve()
      this.couponLoading = true
      this.couponError = ''
      return this.$store.dispatch('fetchValidatedCoupons', { promoCode: code, isRegisterPage: 0 })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            const coupons = data.coupons || []
            const coupon = coupons.length === 1 ? coupons[0] : null
            if (
              coupon &&
              ['Percent Off', 'Amount Off'].includes(coupon.couponType) &&
              coupon.product === POP_AUTHORITY_PACKAGES_COUPON_PRODUCT &&
              !coupon.exceeded
            ) {
              // an amount coupon is valid on every package, so it can be worth more than this one
              const info = popAuthorityCouponFromValidated(code, coupon)
              if (popAuthorityCouponExceedsPrice(this.selectedPackage.price, info)) {
                this.couponError = COUPON_OVER_PRICE_ERROR
                return
              }
              this.appliedCoupon = { code: code.toUpperCase(), coupon }
              showPopAuthorityToast(this, `Coupon ${code.toUpperCase()} applied: ${this.couponLabel}`)
              return
            }
            this.couponError = 'That code is not valid for POP Authority packages.'
            return
          }
          this.couponError = (data && (data.errorMsg || data.msg)) || 'That code is not valid or has expired.'
        })
        .catch(() => {
          this.couponError = 'That code is not valid or has expired.'
        })
        .finally(() => {
          this.couponLoading = false
        })
    },
    removeCoupon () {
      this.appliedCoupon = null
      this.couponCode = ''
      this.couponError = ''
      this.showCoupon = false
    },
    subscribe () {
      if (this.state.agreementRequired && !this.agreementAccepted) return
      const body = {
        ...this.selectionBody(),
        couponCode: this.appliedCoupon ? this.appliedCoupon.code : undefined,
        agreementAccepted: this.agreementAccepted,
        redirectFrontUrl: `/#/sites/${this.siteId}/overview`,
        // checkout abandoned: back to where it started
        redirectFrontForCancelUrl: this.embedded && this.$store.state.user
          ? `/#/useraccount/${this.$store.state.user.id}?mainTab=billing-and-payments&subTab=change-plan`
          : `/#/sites/${this.siteId}/packages`,
      }
      this.run('startPopAuthoritySubscriptionCheckout', { body }).then((data) => {
        if (data && data.url) window.location.href = data.url
      })
    },
    assign () {
      const body = {
        ...this.selectionBody(),
        couponCode: this.appliedCoupon ? this.appliedCoupon.code : undefined,
      }
      this.run('assignPopAuthoritySubscription', { body }, 'Package assigned, invoice sent').then((data) => {
        this.showAssignConfirm = false
        if (data) {
          this.removeCoupon()
          this.applyState(data)
        }
      })
    },
    markPaid () {
      this.run('markPopAuthoritySubscriptionPaid', {}, 'Invoice marked as paid, month 1 started').then((data) => {
        this.showMarkPaidConfirm = false
        if (data) this.applyState(data)
      })
    },
    changePackage () {
      const body = {
        ...this.selectionBody(),
        couponCode: this.appliedCoupon ? this.appliedCoupon.code : undefined,
      }
      this.run('changePopAuthoritySubscriptionPackage', { body }).then((data) => {
        this.showChangeConfirm = false
        this.showReplaceConfirm = false
        if (!data) return
        this.removeCoupon()
        const applied = data.changeResult && data.changeResult.applied
        const texts = {
          now: 'Package changed, the new quota applies now',
          invoice_replaced: 'Package changed, your new invoice has been emailed',
        }
        showPopAuthorityToast(this, texts[applied] || 'Package change scheduled for your next invoice')
        this.applyState(data)
      })
    },
    cancelSubscription ({ reason, feedback }) {
      this.run('cancelPopAuthoritySubscription', { body: { reason, feedback } }, 'Cancellation scheduled').then((data) => {
        this.showCancelModal = false
        if (data) this.applyState(data)
      })
    },
    undoCancel () {
      this.run('undoCancelPopAuthoritySubscription', {}, 'Your subscription continues').then((data) => {
        if (data) this.applyState(data)
      })
    },
    pause () {
      this.run('pausePopAuthoritySubscription', {}, 'Subscription paused').then((data) => {
        if (data) this.applyState(data)
      })
    },
    resume () {
      this.run('resumePopAuthoritySubscription', {}, 'Subscription resumed').then((data) => {
        if (data) this.applyState(data)
      })
    },
    reopen () {
      this.run('reopenPopAuthoritySite', {}, 'Project reopened').then((data) => {
        if (data) this.applyState(data)
      })
    },
  },
}
</script>

<style lang="scss">
/* The global `* { font-size: .85rem; line-height: 18px }` rule (assets/main.scss) would override the
   .9rem / 1.45 base of the design (pa.css .pa-app) on every descendant; inherit instead (icon fonts
   keep their own size). Unscoped on purpose: the rule must reach the children's DOM. */
.pa-packages :where(*:not(.material-icons):not(.material-icons-outlined):not(.material-icons-round):not(.mdi):not(.fa)) {
  font-size: inherit;
  line-height: inherit;
}
</style>

<style lang="scss" scoped>
/* Design pa-tabs.jsx PackageChooser + pa.css "packages" (tokens, buttons, pills, notes, modals from setup.scss). */
@import './setup/setup.scss';
@import './setup/designModal.scss';

$pa-lavender: #f7f5fb; // --wg-lavender (summary panel)
$pa-ink-strong: #171717; // --pop-ink-strong (guarantee strip)

/* Full width like the project pages (Layout.vue): a 1440px cap left a wide empty band on large screens */
.pa-packages {
  padding: 20px 20px 40px;
  color: $pa-teal;
  font-size: 0.9rem;
  line-height: 1.45;

  /* inside Account settings > Plan: the tab already has its own gutters */
  &--embedded {
    padding: 0 0 24px;
  }

  &__back-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  /* pa.css .pa-panel with the design's inline layout: icon + "Subscribe for project" select */
  &__project {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
    max-width: 640px;
    padding: 14px 18px;
    border: 1px solid $pa-border-soft;
    border-radius: 12px;
    background: #fff;

    > .material-icons {
      color: $pa-accent;
    }
  }

  &__project-field {
    flex: 1;
    min-width: 0;
  }

  &__project-label {
    display: block;
    font-weight: 800;
    font-size: 0.9rem;
    margin-bottom: 6px;
  }

  &__loading {
    margin: 16px 0;
  }

  /* design: "How billing works" | summary, 32px apart */
  &__bottom {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(420px, 560px);
    gap: 32px;
    align-items: start;

    @media (max-width: 1000px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  &__billing {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;

    .pa-tlh {
      margin: 0 0 -10px;
    }
  }

  &__billing-text {
    color: $pa-muted;
    font-size: 0.88rem;
    margin: 0;
  }
}

/* pa.css .pa-back */
.pa-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $pa-teal;
  font-weight: 700;
  cursor: pointer;
  margin-bottom: 14px;
  text-decoration: none;

  .material-icons {
    font-size: 1.2rem;
  }

  &:hover {
    color: $pa-accent;
  }
}

/* pa.css .pa-h1 / .pa-sub (the design gives this page's subtitle a 12px bottom margin) */
.pa-h1 {
  font-size: 1.9rem;
  font-weight: 800;
  line-height: 1.45;
  margin: 0 0 6px;
  color: $pa-teal;
}

.pa-sub {
  color: $pa-muted;
  margin: 0 0 12px;
}

.pa-tlh {
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.45;
  margin: 0 0 6px;
  color: $pa-teal;
}

/* design: guarantee strip above the cards */
.pa-guarantee {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff3ea;
  border-radius: 12px;
  padding: 14px 22px;
  font-size: 1.05rem;
  line-height: 1.45;
  color: $pa-ink-strong;
  margin-bottom: 6px;

  .material-icons {
    color: $pa-danger;
    font-size: 20px;
  }

  b {
    font-size: 1.05rem;
  }

  a {
    font-weight: 400;
    font-size: 1.05rem;
    color: $pa-link;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* Invoice card (same look as the setup fee step; no design counterpart on this page) */
.pa-invoice {
  border: 1px solid $pa-border-soft;
  border-radius: 12px;
  background: #fff;
  margin: 18px 0;

  &__head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid $pa-border-soft;

    h3 {
      margin: 0;
      font-size: 1.15rem;
      font-weight: 800;
      color: $pa-teal;
    }
  }

  &__icon {
    font-size: 30px;
    color: $pa-accent;
  }

  &__heading {
    flex: 1;
    min-width: 200px;
  }

  &__sub {
    font-size: 0.88rem;
    color: $pa-muted;
  }

  &__body {
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
  }

  &__actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 16px;
  }
}

.pa-invrow {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px dashed $pa-border-soft;
  font-size: 0.92rem;

  &:last-of-type {
    border-bottom: none;
  }

  b {
    font-weight: 800;
  }
}

.pa-invnote {
  background: $pa-box-bg;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  color: $pa-muted;
  margin-top: 10px;
}

/* Current package (change-package state; the Overview widget holds the design version) */
.pa-current {
  &__coupon {
    display: block;
    margin-top: 2px;
    font-size: 0.78rem;
    font-weight: 600;
    color: $pa-muted;
  }

  border: 1px solid $pa-border-soft;
  border-radius: 12px;
  background: #fff;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 18px 0 8px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  &__eyebrow {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $pa-muted;
    font-weight: 700;
  }

  &__name {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    color: $pa-teal;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 10px;

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;
      background: $pa-box-bg;
      border: 1px solid $pa-border-soft;
      border-radius: 10px;
      padding: 8px 12px;

      span {
        font-size: 0.75rem;
        color: $pa-muted;
      }

      b {
        font-size: 0.9rem;
        font-weight: 800;
      }
    }
  }

  &__actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
}

/* pa.css .pa-summary ("Your subscription") */
.pa-summary {
  border: 1px solid $pa-border-soft;
  border-radius: 12px;
  background: $pa-lavender;
  padding: 18px 20px;
  min-width: 0;

  &__title {
    margin: 0 0 10px;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.45;
    color: $pa-teal;
  }

  &__line {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 6px 0;
    font-size: 0.92rem;

    > span:first-child {
      color: $pa-muted;
    }

    > b {
      font-weight: 800;
      text-align: right;
    }

    &--total {
      border-top: 1px solid $pa-border;
      margin-top: 6px;
      padding-top: 12px;
      font-weight: 800;
      font-size: 1rem;
    }
  }

  &__paid {
    color: $pa-success;
  }

  &__due {
    color: $pa-accent;
  }

  /* design: custom mix that duplicates a standard package */
  &__blocked {
    margin: 4px 0 6px;
    padding: 10px 12px;
    background: #fff3ea;
    border-radius: 10px;
    font-size: 0.86rem;

    b {
      font-weight: 800;
    }
  }

  &__blocked-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 700;
    color: $pa-link;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
  }

  &__note {
    margin: 10px 0 0;
    font-size: 0.82rem;
    color: $pa-muted;
  }

  /* coupon box: blue link toggle (the shared .pa-link look), input + outlined button */
  &__coupon-link {
    margin-top: 14px;
  }

  &__coupon-toggle:hover {
    text-decoration: underline;
  }

  &__coupon {
    display: flex;
    gap: 10px;
    margin-top: 14px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__coupon-input {
    max-width: 220px;
    text-transform: uppercase;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 16px;

    .pa-btn {
      width: 100%;
    }
  }

  /* design PackageChooser: note under the subscribe button */
  &__foot {
    margin: 8px 0 0;
    font-size: 0.8rem;
    color: $pa-faint;
  }
}

</style>
